import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { del, get, post, put } from '../api/client';
import { fleetCmd, isMissing, qk, stopRobots, useCatalog, useGroups, useMaps, useSites } from '../api/queries';
import type { Group, Lang, PerRobotResult, Robot, SiteRef, Tour } from '../api/types';
import { MoveToSiteButton, SiteBadge, SitesModal } from '../components/Site';
import { PageHeader } from '../components/Layout';
import { IconBolt, IconMic, IconMuted, IconSpeaker } from '../components/Icon';
import { Battery, Checkbox, Modal } from '../components/ui';
import { Select } from '../components/Select';
import { AutonomyChip } from '../components/Autonomy';
import { DockMenu } from '../components/Charge';
import { ModeChip } from '../components/ModeChip';
import { SensorMini } from '../components/SensorMini';
import { HandPushButton } from '../components/HandPush';
import { HandPushSummary, IconHand } from '../components/HandPushFab';
import { GuardTileSwitch } from '../components/Guard';
import { useDockTitle } from '../lib/charge';
import { RobotAudio, RobotVideo } from '../components/Video';
import { perRobotToast, toast, toastErr } from '../components/toast';
import { useT } from '../i18n';
import { stopAllTalk, useMedia, useWatch } from '../live/media';
import { useLive, useRobotList } from '../live/store';
import { ago, robotTask } from '../lib/format';
import { useInView, usePopoverClose, useStoredState } from '../lib/hooks';
import { rememberFocus, useSelection } from '../lib/selection';
import { talk } from '../lib/talk';

/** §28: the Fleet's site tab — every robot, one site, or the robots without a site. */
type SiteTab = 'all' | 'none' | string;

export default function Fleet() {
  const t = useT();
  const robots = useRobotList();
  const groups = useGroups();
  const sites = useSites();
  const [filter, setFilter] = useState<string | null>(null);
  const [siteTab, setSiteTab] = useStoredState<SiteTab>('cruzr.fleetSite', 'all');
  const [groupModal, setGroupModal] = useState<{ group: Group | null } | null>(null);
  const [sitesOpen, setSitesOpen] = useState(false);
  const selected = useSelection((s) => s.selected);

  // Stop talking when leaving the Fleet view.
  useEffect(() => () => stopAllTalk(), []);

  // the sites robots are at (live, from the robot list), then any empty ones from the sites list
  const siteList = useMemo(() => {
    const m = new Map<string, SiteRef>();
    for (const r of robots) if (r.site) m.set(r.site.id, r.site);
    for (const x of sites.data ?? []) if (!m.has(x.id)) m.set(x.id, x);
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [robots, sites.data]);
  const tab: SiteTab = siteTab === 'all' || siteTab === 'none' || siteList.some((x) => x.id === siteTab) ? siteTab : 'all';
  const group = groups.data?.find((g) => g.id === filter) ?? null;
  const shown = useMemo(() => {
    let list = group ? robots.filter((r) => group.serials.includes(r.serial)) : robots;
    if (tab === 'none') list = list.filter((r) => !r.site);
    else if (tab !== 'all') list = list.filter((r) => r.site?.id === tab);
    return [...list].sort((a, b) => Number(b.online) - Number(a.online));
  }, [robots, group, tab]);
  const online = robots.filter((r) => r.online).length;
  const count = (pred: (r: Robot) => boolean) => {
    const rs = robots.filter(pred);
    return { n: rs.length, on: rs.filter((r) => r.online).length };
  };
  const noSite = count((r) => !r.site);
  // "All" with sites in use: one section per site (+ the robots without one)
  const sections = tab === 'all' && siteList.length > 0
    ? [...siteList.map((x) => ({ key: x.id, site: x as SiteRef | null, robots: shown.filter((r) => r.site?.id === x.id) })), { key: 'none', site: null, robots: shown.filter((r) => !r.site) }].filter((x) => x.robots.length)
    : null;
  const tabBtn = (id: SiteTab, label: string, c: { n: number; on: number }, color?: string) => (
    <button key={id} type="button" role="tab" aria-selected={tab === id} className="site-tab" onClick={() => setSiteTab(id)}>
      {color && <span className="sq" style={{ background: color }} aria-hidden />}
      {label}
      <span className="mono n">{c.n}</span>
      <span className="on">● {c.on}</span>
    </button>
  );

  return (
    <>
      <PageHeader title={t('fleet.title')}>
        <span className="page-sub">{t('fleet.count', { total: robots.length, online })}</span>
        <HandPushSummary />
        <div className="grow" />
      </PageHeader>
      <div className="fleet-filters">
        <div role="tablist" aria-label={t('site.tabs')} className="site-tabs">
          <span className="section-label hide-mobile">{t('site.label')}</span>
          {tabBtn('all', t('fleet.all'), { n: robots.length, on: online })}
          {siteList.map((x) => tabBtn(x.id, x.name, count((r) => r.site?.id === x.id), x.color))}
          {siteList.length > 0 && noSite.n > 0 && tabBtn('none', t('site.noneShort'), noSite)}
          <button type="button" className="chip sm dashed" onClick={() => setSitesOpen(true)}>
            {t('site.manageShort')}
          </button>
        </div>
        <div className="grow" />
        <div className="row" style={{ gap: 6 }}>
          <Select
            size="sm"
            style={{ width: 180 }}
            ariaLabel={t('fleet.filterByGroup')}
            value={filter ?? ''}
            onChange={(v) => setFilter(v || null)}
            options={[{ value: '', label: t('site.allGroups') }, ...(groups.data ?? []).map((g) => ({ value: g.id, label: g.name, meta: String(g.serials.length) }))]}
          />
          {group && (
            <button type="button" className="chip sm dashed" onClick={() => setGroupModal({ group })}>
              {t('fleet.editGroup')}
            </button>
          )}
          <button type="button" className="chip sm dashed" onClick={() => setGroupModal({ group: null })}>
            {t('fleet.addGroup')}
          </button>
        </div>
      </div>
      {selected.length > 0 && <BulkBar selected={selected} />}
      {sections ? (
        <div className="fleet-sections">
          {sections.map((sec) => {
            const on = sec.robots.filter((r) => r.online).length;
            return (
              <section key={sec.key} className="fleet-section" aria-label={sec.site?.name ?? t('site.noneShort')}>
                <div className="fleet-section-head">
                  {sec.site ? <span className="sq" style={{ background: sec.site.color }} aria-hidden /> : <span className="sq none" aria-hidden />}
                  <h2 className="h2">{sec.site?.name ?? t('site.noneShort')}</h2>
                  <span className="muted" style={{ fontSize: 13 }}>
                    {t('site.sectionCount', { n: sec.robots.length })} · <span style={{ color: 'var(--green)' }}>{t('site.sectionOnline', { n: on })}</span>
                  </span>
                  <span className="line" aria-hidden />
                  <button type="button" className="link-btn" onClick={() => setSiteTab(sec.site?.id ?? 'none')}>
                    {t('site.onlyThis', { site: sec.site?.name ?? t('site.noneShort') })} →
                  </button>
                </div>
                <div className="fleet-grid in-section">
                  {sec.robots.map((r) => (
                    <RobotTile key={r.serial} robot={r} />
                  ))}
                </div>
              </section>
            );
          })}
          {!shown.length && <div className="empty">{robots.length ? t('fleet.emptyGroup') : t('fleet.empty')}</div>}
        </div>
      ) : (
        <div className="fleet-grid">
          {shown.map((r) => (
            <RobotTile key={r.serial} robot={r} />
          ))}
          {!shown.length && <div className="empty">{robots.length ? t('fleet.emptyGroup') : t('fleet.empty')}</div>}
        </div>
      )}
      {groupModal && <GroupModal group={groupModal.group} onClose={() => setGroupModal(null)} onSaved={(id) => setFilter(id)} />}
      {sitesOpen && <SitesModal onClose={() => setSitesOpen(false)} />}
    </>
  );
}

function RobotTile({ robot: r }: { robot: Robot }) {
  const t = useT();
  const nav = useNavigate();
  const [ref, inView] = useInView<HTMLElement>();
  useWatch(r.serial, r.online && inView);
  const sel = useSelection((s) => s.selected.includes(r.serial));
  const toggle = useSelection((s) => s.toggle);
  const sound = useMedia((s) => Boolean(s.tileSound[r.serial]));
  const toggleSound = useMedia((s) => s.toggleTileSound);
  const info = useMedia((s) => s.byRobot[r.serial]);
  const st = r.status ?? {};
  const charging = Boolean(st.charging || st.docked);
  const lowBattery = typeof st.battery === 'number' && st.battery < 40;
  const [busy, setBusy] = useState(false);
  const dockTitle = useDockTitle(st.currentMap);

  const open = () => {
    rememberFocus(r.serial);
    nav(`/robots/${encodeURIComponent(r.serial)}`);
  };
  // Charger button: go to charger → (while driving there) cancel → (docked) leave charger.
  const docking = busy || st.task?.kind === 'dock';
  const dockAt = async (pointId?: string) => {
    setBusy(true);
    try {
      perRobotToast(t('fleet.goToCharger'), (await fleetCmd({ robots: [r.serial] }, 'dock', pointId ? { pointId } : {})).results);
    } catch (e) {
      toastErr(e);
    } finally {
      setBusy(false);
    }
  };
  const chargerAction = async () => {
    if (charging) {
      try {
        perRobotToast(t('fleet.leaveCharger'), (await fleetCmd({ robots: [r.serial] }, 'undock')).results);
      } catch (e) {
        toastErr(e);
      }
      return;
    }
    if (docking) {
      try {
        await fleetCmd({ robots: [r.serial] }, 'stop');
      } catch (e) {
        toastErr(e);
      }
      return;
    }
    setBusy(true);
    try {
      perRobotToast(t('fleet.goToCharger'), (await fleetCmd({ robots: [r.serial] }, 'dock')).results);
    } catch (e) {
      toastErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      ref={ref}
      className={`tile${sel ? ' sel' : ''}${r.online ? '' : ' offline'}${r.online && st.handPush?.enabled ? ' released' : ''}`}
      aria-label={r.site ? `${r.name} · ${r.site.name}` : r.name}
      style={r.site ? { borderTop: `3px solid ${r.site.color}` } : undefined}
    >
      <div className="tile-video" onClick={open} role="link" tabIndex={-1}>
        {r.site && <SiteBadge site={r.site} small className="tile-site" />}
        {r.online ? <RobotVideo serial={r.serial} online /> : <span style={{ fontSize: 13, color: 'var(--muted-2)' }}>{t('fleet.offlineSeen', { ago: ago(r.lastSeen) })}</span>}
        {r.online && <RobotAudio serial={r.serial} enabled={sound} />}
        {r.online && st.handPush?.enabled && (
          <span className="tile-released">
            <IconHand size={14} />
            {t('fab.wheelsReleased')}
          </span>
        )}
        {r.online && <SensorMini serial={r.serial} size={100} className="tile-radar" />}
        {r.online && (
          <button
            type="button"
            className={`tile-sound${sound ? ' on' : ''}`}
            aria-pressed={sound}
            aria-label={t('fleet.hear', { name: r.name })}
            onClick={(e) => {
              e.stopPropagation();
              toggleSound(r.serial);
            }}
          >
            {sound ? <IconSpeaker /> : <IconMuted />}
            {sound ? t('fleet.soundOn') : t('fleet.muted')}
          </button>
        )}
        <button
          type="button"
          className="tile-check"
          aria-pressed={sel}
          aria-label={t('fleet.select', { name: r.name })}
          onClick={(e) => {
            e.stopPropagation();
            toggle(r.serial);
          }}
        >
          <Checkbox on={sel} />
        </button>
      </div>
      <div className="tile-body">
        <div className="row" style={{ gap: 8 }}>
          <span className={`dot ${r.online ? 'green' : ''}`} />
          <Link to={`/robots/${encodeURIComponent(r.serial)}`} className="tile-name ellipsis" onClick={() => rememberFocus(r.serial)}>
            {r.name}
          </Link>
          <div className="grow" />
          <Battery pct={st.battery} charging={st.charging} />
        </div>
        {r.online && (
          <div className="tile-mode">
            <ModeChip serial={r.serial} compact />
          </div>
        )}
        <div className="tile-meta">
          <span className="ellipsis">{st.currentMap ?? '—'}</span>
          <span>·</span>
          <span className="ellipsis" style={{ color: 'var(--text-2)' }}>
            {robotTask(r)}
          </span>
          <span className="grow" />
          <AutonomyChip serial={r.serial} online={r.online} />
        </div>
        {r.online && <GuardTileSwitch serial={r.serial} />}
        {r.online && (
          <div className="tile-actions">
            <button
              type="button"
              className={`btn${info?.talking ? ' on' : ''}`}
              aria-pressed={Boolean(info?.talking)}
              aria-label={t('fleet.talkThrough', { name: r.name })}
              disabled={info?.state !== 'connected'}
              onClick={() => void talk(r.serial, !info?.talking)}
              title={info?.talking ? t('talk.talking') : t('talk.talk')}
            >
              <IconMic />
              <span className="btn-label">{info?.talking ? t('talk.talking') : t('talk.talk')}</span>
            </button>
            <span className="split">
              <button
                type="button"
                className="btn"
                disabled={!r.online}
                onClick={() => void chargerAction()}
                title={charging ? `${t('fleet.leaveCharger')} — ${t('fleet.onCharger')}` : docking ? t('fleet.cancelCharger') : `${t('fleet.goToCharger')} — ${dockTitle}`}
                style={{ color: docking ? 'var(--danger-text, #FF8A80)' : !charging && lowBattery ? 'var(--accent)' : undefined }}
              >
                <IconBolt />
                <span className="btn-label">
                  {charging ? (
                    t('fleet.leaveCharger')
                  ) : docking ? (
                    t('fleet.cancelCharger')
                  ) : (
                    <>
                      <span className="lbl-full">{t('fleet.goToCharger')}</span>
                      <span className="lbl-short">{t('fleet.chargeShort')}</span>
                    </>
                  )}
                </span>
              </button>
              {!charging && !docking && <DockMenu serial={r.serial} mapName={st.currentMap} disabled={!r.online} onDock={(pointId) => void dockAt(pointId)} />}
            </span>
            <HandPushButton serial={r.serial} online={r.online} />
            <button type="button" className="btn" onClick={open} title={t('fleet.control')}>
              <span className="btn-label">{t('fleet.control')}</span>
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

function BulkBar({ selected }: { selected: string[] }) {
  const t = useT();
  const robots = useLive((s) => s.robots);
  const clear = useSelection((s) => s.clear);
  const media = useMedia((s) => s.byRobot);
  const [text, setText] = useState('');
  const [lang, setLang] = useStoredState<Lang>('cruzr.sayLang', 'ru');
  const [menu, setMenu] = useState<'tour' | 'dance' | 'volume' | null>(null);
  const onlineSel = selected.filter((s) => robots[s]?.online);
  const talkingAll = onlineSel.length > 0 && onlineSel.every((s) => media[s]?.talking);

  const run = async (what: string, fn: () => Promise<{ results: PerRobotResult[] }>) => {
    if (!onlineSel.length) return toast(t('fleet.noneOnlineSelected'), 'err');
    try {
      perRobotToast(what, (await fn()).results);
    } catch (e) {
      toastErr(e, what);
    }
  };
  const say = (e: FormEvent) => {
    e.preventDefault();
    const txt = text.trim();
    if (!txt) return;
    setText('');
    void run(t('fleet.say'), () => fleetCmd({ robots: onlineSel }, 'say', { text: txt, lang }));
  };
  const toggleTalk = async () => {
    const on = !talkingAll;
    await Promise.all(onlineSel.map((s) => talk(s, on)));
  };

  return (
    <div role="toolbar" aria-label={t('fleet.bulkLabel')} className="bulkbar">
      <span className="count">{t('fleet.selected', { n: selected.length })}</span>
      <form className="say" onSubmit={say}>
        <label className="sr-only" htmlFor="bulk-say">
          {t('fleet.sayToSelected')}
        </label>
        <input id="bulk-say" value={text} onChange={(e) => setText(e.target.value)} placeholder={t('fleet.sayPlaceholder')} />
        <Select<Lang>
          size="sm"
          style={{ width: 64, flexShrink: 0 }}
          ariaLabel={t('speech.language')}
          value={lang}
          onChange={setLang}
          options={[
            { value: 'uz', label: 'uz' },
            { value: 'ru', label: 'ru' },
            { value: 'en', label: 'en' },
          ]}
        />
        <button type="submit" className="btn primary sm" style={{ height: 32 }}>
          {t('fleet.say')}
        </button>
      </form>
      <div className="pop-anchor">
        <button type="button" className="btn" onClick={() => setMenu(menu === 'tour' ? null : 'tour')} aria-expanded={menu === 'tour'}>
          {t('fleet.runTour')}
        </button>
        {menu === 'tour' && <TourMenu serials={onlineSel} onClose={() => setMenu(null)} />}
      </div>
      <div className="pop-anchor">
        <button type="button" className="btn" onClick={() => setMenu(menu === 'dance' ? null : 'dance')} aria-expanded={menu === 'dance'}>
          {t('fleet.dance')}
        </button>
        {menu === 'dance' && (
          <DanceMenu
            onPick={(id) => {
              setMenu(null);
              void run(t('fleet.danceWhat', { id }), () => fleetCmd({ robots: onlineSel }, 'dance', { id }));
            }}
            onClose={() => setMenu(null)}
          />
        )}
      </div>
      <div className="pop-anchor">
        <button type="button" className="btn" onClick={() => setMenu(menu === 'volume' ? null : 'volume')} aria-expanded={menu === 'volume'}>
          {t('volume.bulk')}
        </button>
        {menu === 'volume' && (
          <VolumeMenu
            onApply={(level) => {
              setMenu(null);
              void run(t('volume.what', { n: level }), () => fleetCmd({ robots: onlineSel }, 'setVolume', { level }));
            }}
            onClose={() => setMenu(null)}
          />
        )}
      </div>
      <button type="button" className="btn" title={t('charge.dockTitle')} onClick={() => void run(t('fleet.goToCharger'), () => fleetCmd({ robots: onlineSel }, 'dock'))}>
        <IconBolt />
        {t('fleet.goToCharger')}
      </button>
      <button type="button" className={`btn${talkingAll ? ' on' : ''}`} aria-pressed={talkingAll} onClick={() => void toggleTalk()}>
        <IconMic />
        {talkingAll ? t('fleet.talkingTo', { n: onlineSel.length }) : t('fleet.talkToSelected')}
      </button>
      <MoveToSiteButton serials={selected} />
      <div className="grow" />
      <button type="button" className="btn ghost sm" onClick={clear}>
        {t('fleet.clearSelection')}
      </button>
      <button type="button" className="btn danger" onClick={() => void run(t('fleet.stopSelected'), () => stopRobots(onlineSel))}>
        {t('fleet.stopSelected')}
      </button>
    </div>
  );
}

function VolumeMenu({ onApply, onClose }: { onApply: (level: number) => void; onClose: () => void }) {
  const t = useT();
  const ref = usePopoverClose(true, onClose);
  const [level, setLevel] = useState(60);
  return (
    <div ref={ref} className="popover" style={{ top: 46, left: 0, width: 260 }}>
      <label className="range-field">
        <span className="top">
          {t('volume.speaker')} <span className="val">{level}%</span>
        </span>
        <input type="range" min={0} max={100} step={5} value={level} onChange={(e) => setLevel(Number(e.target.value))} />
      </label>
      <div className="row">
        <button type="button" className="btn sm" onClick={() => onApply(0)}>
          {t('volume.mute')}
        </button>
        <div className="grow" />
        <button type="button" className="btn sm primary" onClick={() => onApply(level)}>
          {t('volume.apply')}
        </button>
      </div>
    </div>
  );
}

function DanceMenu({ onPick, onClose }: { onPick: (id: string) => void; onClose: () => void }) {
  const catalog = useCatalog();
  const ref = usePopoverClose(true, onClose);
  return (
    <div ref={ref} className="popover" style={{ top: 46, left: 0 }}>
      <div className="menu-list">
        {catalog.data?.dances.map((d) => (
          <button key={d} type="button" onClick={() => onPick(d)}>
            {d}
          </button>
        ))}
      </div>
    </div>
  );
}

function TourMenu({ serials, onClose }: { serials: string[]; onClose: () => void }) {
  const t = useT();
  const ref = usePopoverClose(true, onClose);
  const robots = useLive((s) => s.robots);
  const maps = useMaps();
  const mapNames = [...new Set(serials.map((s) => robots[s]?.status?.currentMap).filter((m): m is string => Boolean(m)))];
  const mapIds = (maps.data ?? []).filter((m) => mapNames.includes(m.name)).map((m) => m.id);
  const tours = useQueries({
    queries: mapIds.map((id) => ({ queryKey: qk.tours(id), queryFn: () => get<Tour[]>(`/api/maps/${id}/tours`) })),
  });
  const missing = tours.some((q) => isMissing(q.error));
  const list = tours.flatMap((q) => q.data ?? []);
  const runTour = async (tour: Tour) => {
    onClose();
    try {
      const r = await post<{ results?: PerRobotResult[] }>(`/api/tours/${tour.id}/run`, { robots: serials });
      if (r.results) perRobotToast(t('fleet.tourWhat', { name: tour.name }), r.results);
      else toast(t('fleet.tourStarted', { name: tour.name }), 'ok');
    } catch (e) {
      toastErr(e, tour.name);
    }
  };
  return (
    <div ref={ref} className="popover" style={{ top: 46, left: 0, width: 280 }}>
      {missing ? (
        <span className="muted" style={{ fontSize: 13 }}>
          {t('tours.apiMissing')}
        </span>
      ) : !mapIds.length ? (
        <span className="muted" style={{ fontSize: 13 }}>
          {t('fleet.noMapForTours')}
        </span>
      ) : list.length ? (
        <div className="menu-list">
          {list.map((tour) => (
            <button key={tour.id} type="button" onClick={() => void runTour(tour)}>
              {tour.name} <span className="muted">· {t('tours.stopsN', { n: tour.stops.length })}</span>
            </button>
          ))}
        </div>
      ) : (
        <span className="muted" style={{ fontSize: 13 }}>
          {tours.some((q) => q.isLoading) ? t('common.loading') : t('fleet.noTours')}
        </span>
      )}
    </div>
  );
}

function GroupModal({ group, onClose, onSaved }: { group: Group | null; onClose: () => void; onSaved: (id: string | null) => void }) {
  const t = useT();
  const qc = useQueryClient();
  const robots = useRobotList();
  const selected = useSelection((s) => s.selected);
  const [name, setName] = useState(group?.name ?? '');
  const [members, setMembers] = useState<string[]>(group ? group.serials : selected);
  const [busy, setBusy] = useState(false);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const g = group ? await put<Group>(`/api/groups/${group.id}`, { name: name.trim(), serials: members }) : await post<Group>('/api/groups', { name: name.trim(), serials: members });
      await qc.invalidateQueries({ queryKey: qk.groups });
      toast(t('fleet.groupSaved', { name: g.name }), 'ok');
      onSaved(g.id);
      onClose();
    } catch (err) {
      toastErr(err);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!group || !confirm(t('fleet.deleteGroupConfirm', { name: group.name }))) return;
    try {
      await del(`/api/groups/${group.id}`);
      await qc.invalidateQueries({ queryKey: qk.groups });
      onSaved(null);
      onClose();
    } catch (err) {
      toastErr(err);
    }
  };
  return (
    <Modal title={group ? t('fleet.editGroupTitle') : t('fleet.newGroupTitle')} onClose={onClose}>
      <form className="col" style={{ gap: 14 }} onSubmit={save}>
        <label className="field">
          {t('fleet.groupName')}
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <div className="col" style={{ gap: 4, maxHeight: 300, overflow: 'auto' }}>
          <span className="section-label">{t('fleet.members')}</span>
          {robots.map((r) => (
            <label key={r.serial} className="row" style={{ height: 36, fontSize: 14 }}>
              <input type="checkbox" checked={members.includes(r.serial)} onChange={(e) => setMembers(e.target.checked ? [...members, r.serial] : members.filter((s) => s !== r.serial))} />
              <span className="grow">{r.name}</span>
              <span className="mono muted" style={{ fontSize: 11 }}>
                {r.serial}
              </span>
            </label>
          ))}
        </div>
        <div className="dialog-actions">
          {group && (
            <button type="button" className="btn ghost" style={{ marginRight: 'auto', color: 'var(--red-soft)' }} onClick={() => void remove()}>
              {t('common.delete')}
            </button>
          )}
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
